import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import AdminLayout from '../components/AdminLayout';
import { api } from '../../api';
import { useAdminUser } from '../AdminUserContext';

function StatCard({ label, value, sub, color = '' }) {
  return (
    <div className={`a-stat-card ${color}`}>
      <div className="a-stat-label">{label}</div>
      <div className="a-stat-value">{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--a-muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Bar({ label, value, max, color = 'var(--a-gold)' }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ color: 'var(--a-muted)' }}>{value} ({pct}%)</span>
      </div>
      <div style={{ background: 'var(--a-surface2)', borderRadius: 4, height: 7, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s ease' }} />
      </div>
    </div>
  );
}

const LANG_LABELS = {
  ar: 'العربية', en: 'English', fr: 'Français', de: 'Deutsch', es: 'Español',
  ru: 'Русский', zh: '中文', tr: 'Türkçe', id: 'Indonesia', ms: 'Melayu',
  ur: 'اردو', fa: 'فارسی', hi: 'हिन्दी', bn: 'বাংলা', pt: 'Português',
  it: 'Italiano', sw: 'Kiswahili', ha: 'Hausa', yo: 'Yorùbá', am: 'አማርኛ',
  so: 'Soomaali', ta: 'தமிழ்', ml: 'മലയാളം', ne: 'नेपाली', si: 'සිංහල',
  ps: 'پښتو', uz: 'Oʻzbekcha', tg: 'Тоҷикӣ', bs: 'Bosanski', sv: 'Svenska',
  lt: 'Lietuvių', pa: 'ਪੰਜਾਬੀ', fil: 'Filipino', ti: 'ትግርኛ', om: 'Oromoo', ug: 'ئۇيغۇرچە',
};

export default function Dashboard() {
  const { user } = useAdminUser();
  const isAdmin = user?.role === 'admin' || user?.role === undefined;
  const allowedLangs = user?.allowedLangs || [];

  const [apiStats, setApiStats] = useState({ total: 0, today: 0, topLangs: [] });
  const [streams,  setStreams]  = useState([]);

  // Real-time socket stats
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [audioUsers,  setAudioUsers]  = useState(0);
  const [isPlaying,   setIsPlaying]   = useState(false);
  const [byLang,      setByLang]      = useState({});

  const socketRef = useRef(null);

  useEffect(() => {
    api.getStats().then(data => setApiStats({ ...data, topLangs: data.byLang || [] })).catch(console.error);
    api.getStreams().then(setStreams).catch(console.error);

    const socket = io(api.socketUrl(), { transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('stats:update', ({ onlineUsers: ou, byLang: bl, audioUsers: au, isPlaying: ip }) => {
      setOnlineUsers(ou || 0);
      setByLang(bl || {});
      setAudioUsers(au || 0);
      setIsPlaying(!!ip);
    });

    return () => socket.disconnect();
  }, []);

  const liveStream = streams.find(s => s.isLive);

  const visibleByLang = isAdmin || allowedLangs.length === 0
    ? byLang
    : Object.fromEntries(Object.entries(byLang).filter(([code]) => allowedLangs.includes(code)));

  const visibleTopLangs = isAdmin || allowedLangs.length === 0
    ? (apiStats.topLangs || [])
    : (apiStats.topLangs || []).filter(item => allowedLangs.includes(item.lang));

  const topRealtime = Object.entries(visibleByLang).sort(([, a], [, b]) => b - a).slice(0, 12);
  const maxOnline   = topRealtime[0]?.[1] || 1;

  const displayOnline = isAdmin ? onlineUsers : topRealtime.reduce((sum, [, n]) => sum + n, 0);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Dashboard</h1>
        <p>Real-time platform overview — updates automatically</p>
      </div>

      {/* Live stream status banner */}
      {liveStream && (
        <div style={{ background: liveStream.isLive ? 'rgba(34,197,94,.08)' : 'rgba(239,68,68,.05)', border: `1px solid ${liveStream.isLive ? 'rgba(34,197,94,.3)' : 'rgba(239,68,68,.2)'}`, borderRadius: 10, padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: liveStream.isLive ? '#22c55e' : '#ef4444', flexShrink: 0, animation: liveStream.isLive ? 'a-pulse 1.4s infinite' : 'none' }} />
          <div>
            <span style={{ fontWeight: 700, fontSize: 14, color: liveStream.isLive ? '#22c55e' : '#ef4444' }}>{liveStream.isLive ? 'LIVE NOW' : 'OFFLINE'}</span>
            <span style={{ fontSize: 13, color: 'var(--a-muted)', marginInlineStart: 10 }}>{liveStream.title}</span>
          </div>
          {isPlaying && (
            <span style={{ marginInlineStart: 'auto', fontSize: 13, color: 'var(--a-gold)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="fas fa-music" /> Audio sync active · {audioUsers} listener{audioUsers !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      {/* ── Stats ── */}
      <div className="a-stats-grid" style={{ marginBottom: 28 }}>
        {isAdmin && <StatCard label="Total Visitors" value={(apiStats.total || 0).toLocaleString()} />}
        <StatCard label="Languages Live" value={Object.keys(visibleByLang).length} sub={isAdmin ? 'Languages with active users' : 'Your active languages'} />
        <StatCard label="Broadcast" value={isPlaying ? 'Active' : 'Stopped'} color={isPlaying ? 'green' : ''} sub="Admin audio sync" />
      </div>

      {/* ── Streams (admin only) ── */}
      {isAdmin && <div className="a-table-wrap">
        <div className="a-table-header"><h3>Stream URLs</h3></div>
        <table className="a-table">
          <thead>
            <tr><th>Title</th><th>Year</th><th>Status</th><th>URL</th></tr>
          </thead>
          <tbody>
            {streams.map(s => (
              <tr key={s.id}>
                <td style={{ fontWeight: 600 }}>{s.title}</td>
                <td>{s.year}</td>
                <td>{s.isLive ? <span className="a-badge green"><span className="a-live-dot green" /> Live</span> : <span className="a-badge red">Offline</span>}</td>
                <td><span style={{ fontSize: 11, color: 'var(--a-muted)', wordBreak: 'break-all', maxWidth: 280, display: 'block' }}>{s.url}</span></td>
              </tr>
            ))}
            {streams.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--a-muted)', padding: 28 }}>No streams configured.</td></tr>}
          </tbody>
        </table>
      </div>}

      <style>{`@keyframes a-pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:.6}}`}</style>
    </AdminLayout>
  );
}
